/* eslint-disable max-len */
import { toastr } from 'react-redux-toastr';
import moment from 'moment';
import _ from 'lodash';
import {
    pendingTask,
    begin,
    end,
} from 'react-redux-spinner';
import { push } from 'redux-first-history';
import { applyUkgUserTimeZoneTransformation, convertNullableDateTimeToUkgUserDateTime, removeUkgUserTimeZoneTransformation } from '@commonClient2/services/ukgDateTime';
import {
    RESET_BID_PROFILE,
    UPDATE_BID_PROFILE,
    UPDATE_BID_PROFILE_SCHEDULE,
    UPDATE_ORIGINAL_BID_PROFILE_SCHEDULE,
    UPDATE_SCHEDULE_SELECTION,
    UPDATE_ORIGINAL_SCHEDULE_SELECTION,
    UPDATE_EMPLOYEES_SELECTION,
    UPDATE_ORIGINAL_EMPLOYEES_SELECTION,
    UPDATE_BIDDING_INFORMATION,
    UPDATE_ORIGINAL_BIDDING_INFORMATION,
} from './bidProfile.actions';
import { UPDATE_MANAGER_APP_DATA } from '../appData/appData.actions';
import { 
    UPDATE_DRAFT_BID_PROFILE, 
    UPDATE_DRAFT_SCHEDULE_SELECTION, 
    UPDATE_DRAFT_ORIGINAL_SCHEDULE_SELECTION,
    UPDATE_DRAFT_EMPLOYEES_SELECTION,
    UPDATE_DRAFT_ORIGINAL_EMPLOYEES_SELECTION
} from '../bidProfileDraft';
import { UPDATE_BID_PROFILE_MANAGEMENT } from '../bidProfileManagement/bidProfileManagement.actions';
import { hasManagementAccess as findHasManagementAccess } from '../../BiddingList/BiddingList.service';
import { LOADING_END, LOADING_START } from '../../../store/loading';
import * as api from '../../services/api-endpoints';
import {
    simplifyBidProfileDraftModels,
    simplifyBidProfileModels,
    isScheduleSelectionEditable,
    simplifyAwardingResult,
    simplifyScheduleTemplates,
    simplifySequentialSchedule,
} from '../../services/BidProfileModelMapper';
import { loadEmployees } from '../appData';
import BriefBidProfileModelBuilder from '../../services/BriefBidProfileModelBuilder';
import { BidProfileEditTab, ScheduleSource, FOREVER_EPOCH, FOREVER_ISO_STRING, isForeverDate } from '../../../Enums';
import i18n from '../../../services/i18n';
import FileService from '../../../services/FileService';

export const emptyProfile = () =>/** @param {import('redux').Dispatch} dispatch */(dispatch) => {
    dispatch({ type: RESET_BID_PROFILE });
};

/**
 * @param {string} profileId
 * @param {{
 *  hyperfinds: ManagerAppData.IHyperfind[],
 *  managerBidSetups: ManagerAppData.IManagerBidSetup[],
 *  bidProfiles: BidProfile.IBriefBaseProfile[]
 * }} storeData
 * @param {boolean} hasAccess
 * @param {BidProfileEditTab} tabSelected
 */
// eslint-disable-next-line max-len
export const loadBidProfileEdit = (profileId, storeData, hasAccess, tabSelected) => /** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    dispatch({ type: LOADING_START, [pendingTask]: begin });

    api.getBidProfile(profileId).then((result) => {
        const [bidProfile] = simplifyBidProfileModels([result.data], storeData, tabSelected);
        dispatch({ type: UPDATE_BID_PROFILE, payload: { ...bidProfile } });
        dispatch(loadEmployees(false, bidProfile, { managerBidSetups: storeData.managerBidSetups }));

        if (tabSelected === BidProfileEditTab.Form) {
            dispatch({ type: UPDATE_BIDDING_INFORMATION, payload: { isExpanded: true } });
        }

        const latestStatus = result.data.status;
        const updatedBidProfiles = storeData.bidProfiles;
        const bidProfileIndex = updatedBidProfiles.findIndex((x) => x.id === profileId);
        if (bidProfileIndex !== -1) {
            updatedBidProfiles[bidProfileIndex].status = latestStatus;
        }
        dispatch({
            type: UPDATE_MANAGER_APP_DATA,
            payload: {
                pageName: i18n.t('shiftBidding:manager.common.editBid'),
                bidProfiles: [...updatedBidProfiles],
            },
        });

        /**
         * @type {Promise<*>[]}
         */
        const promises = [api.GetBidProfileScheduleItems(profileId)];
        if (hasAccess) {
            promises.push(api.getBidProfileManagementInfos(profileId));
        }
        Promise.all(promises)
            .then((results) => {
                const canEditScheduleSelection = isScheduleSelectionEditable(bidProfile.status);
                const scheduleTemplates = simplifyScheduleTemplates(results[0].data, canEditScheduleSelection);
                dispatch({ type: UPDATE_BID_PROFILE, payload: { isWorkPlansRetrieved: results[0].data.isCompleted } });

                dispatch({ type: UPDATE_SCHEDULE_SELECTION, payload: scheduleTemplates });
                dispatch({ type: UPDATE_ORIGINAL_SCHEDULE_SELECTION, payload: _.cloneDeep(scheduleTemplates) });

                if (hasAccess) {
                    dispatch({
                        type: UPDATE_BID_PROFILE_MANAGEMENT,
                        payload: {
                            profileId: _.cloneDeep(profileId),
                            employeesBidSelection: results[1].data.employeesBidSelection,
                            awardingResults: simplifyAwardingResult(results[1].data.awardingResults, scheduleTemplates.items),
                            isBidOnBehalfAllowed: results[1].data.isBidOnBehalfAllowed,
                            isEditBidOnBehalfAllowed: results[1].data.isEditBidOnBehalfAllowed,
                            isRecalculateEmployeeRanksAllowed: results[1].data.isRecalculateEmployeeRanksAllowed,
                            isAwardingEmployees: results[1].data.isAwardingEmployees,
                            rolloutErrors: results[1].data.rolloutErrors,
                        },
                    });
                }

                dispatch(push('/Manager/form'));
            })
            .catch((error) => {
                console.error(error);
                toastr.error(i18n.t('shiftBidding:toastr.fetchingBidSubmissions'), error.response.data.Message);
            })
            .finally(() => {
                dispatch({ type: LOADING_END, [pendingTask]: end });
            });
    }).catch((error) => {
        console.error(error);
        toastr.error(i18n.t('shiftBidding:toastr.fetchingBidSubmissions'), error.response.data.Message);
        dispatch({ type: LOADING_END, [pendingTask]: end });
    });
};

/**
 * @param {string} profileId
 * @param {{
 *  hyperfinds: ManagerAppData.IHyperfind[],
 *  managerBidSetups: ManagerAppData.IManagerBidSetup[],
 *  archivedBidProfiles: BidProfile.IBriefBaseProfile[]
 * }} storeData
 */
// eslint-disable-next-line max-len
export const loadBidProfileArchive = (profileId, storeData) => /** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    dispatch({ type: LOADING_START, [pendingTask]: begin });

    api.getBidProfile(profileId).then((result) => {
        const [bidProfile] = simplifyBidProfileModels([result.data], storeData, BidProfileEditTab.Dashboard, true);
        dispatch({ type: UPDATE_BID_PROFILE, payload: { ...bidProfile } });
        dispatch(loadEmployees(false, bidProfile, { managerBidSetups: storeData.managerBidSetups }));

        /**
         * @type {Promise<*>[]}
         */
        const promises = [api.GetBidProfileScheduleItems(profileId)];

        promises.push(api.getBidProfileManagementInfos(profileId));
        Promise.all(promises)
            .then((results) => {
                const scheduleTemplates = simplifyScheduleTemplates(results[0].data, false);
                dispatch({ type: UPDATE_BID_PROFILE, payload: { isWorkPlansRetrieved: results[0].data.isCompleted, isArchive: true } });

                dispatch({ type: UPDATE_SCHEDULE_SELECTION, payload: scheduleTemplates });
                dispatch({ type: UPDATE_ORIGINAL_SCHEDULE_SELECTION, payload: _.cloneDeep(scheduleTemplates) });

                dispatch({
                    type: UPDATE_BID_PROFILE_MANAGEMENT,
                    payload: {
                        profileId: _.cloneDeep(profileId),
                        employeesBidSelection: results[1].data.employeesBidSelection,
                        awardingResults: simplifyAwardingResult(results[1].data.awardingResults, scheduleTemplates.items),
                        isBidOnBehalfAllowed: results[1].data.isBidOnBehalfAllowed,
                        isEditBidOnBehalfAllowed: results[1].data.isEditBidOnBehalfAllowed,
                        isRecalculateEmployeeRanksAllowed: results[1].data.isRecalculateEmployeeRanksAllowed,
                        isAwardingEmployees: results[1].data.isAwardingEmployees,
                        rolloutErrors: results[1].data.rolloutErrors,
                    },
                });

                dispatch(push('/Manager/form'));
            })
            .catch((error) => {
                console.error(error);
                toastr.error(i18n.t('shiftBidding:toastr.fetchingBidSubmissions'), error.response.data.Message);
            })
            .finally(() => {
                dispatch({ type: LOADING_END, [pendingTask]: end });
            });
    }).catch((error) => {
        console.error(error);
        toastr.error(i18n.t('shiftBidding:toastr.fetchingBidSubmissions'), error.response.data.Message);
        dispatch({ type: LOADING_END, [pendingTask]: end });
    });
};

/**
 * @param {string} profileId
 * @param {BidProfile.IBidProfileRebid[] | null} rebidParents
 * @param {{
 *  bidProfiles: BidProfile.IBriefBaseProfile[]
 *  hyperfinds: ManagerAppData.IHyperfind[],
 * }} storeData
 * @returns
 */
export const deleteLatestRebid = (profileId, rebidParents, storeData) => /** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });

        if (!rebidParents || !rebidParents.length) { // Should never happen because it should always have at least one rebidParents
            toastr.error(i18n.t('shiftBidding:toastr.deletingBid'), i18n.t('shiftBidding:toastr.failedToDelete'));
            return;
        }
        const [nextInLine] = rebidParents;
        const response = await api.deleteBidProfile(profileId);

        if (response.status === 200) {
            // Get latest rebid
            const nextInLineProfile = await api.getBidProfile(nextInLine.id, true);
            if (nextInLineProfile.status === 200) {
                const updatedRebidBidProfile = BriefBidProfileModelBuilder.fromBidProfilePresentationModel(nextInLineProfile.data, storeData);
                // Update store with latest rebid with updated rebids
                const updatedBidProfiles = storeData.bidProfiles;
                const deletedRebidBidProfileIndex = updatedBidProfiles.findIndex((x) => x.id === profileId);
                updatedBidProfiles[deletedRebidBidProfileIndex] = updatedRebidBidProfile;
                dispatch({ type: UPDATE_MANAGER_APP_DATA, payload: { bidProfiles: [...updatedBidProfiles] } });
            }
            toastr.success(i18n.t('shiftBidding:toastr.deletingBid'), i18n.t('shiftBidding:toastr.bidDeletedSuccessfully'));
        } else {
            toastr.error(i18n.t('shiftBidding:toastr.deletingBid'), i18n.t('shiftBidding:toastr.failedToDelete'));
        }
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.deletingBid'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {string} profileId
 * @param {BidProfile.IBriefBaseProfile[]} profiles
 * @returns
 */
export const deleteBidProfile = (profileId, profiles) => /** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const response = await api.deleteBidProfile(profileId);

        if (response.status === 200) {
            const bidProfileIndex = profiles.findIndex((x) => x.id === profileId);
            profiles.splice(bidProfileIndex, 1);
            dispatch({ type: UPDATE_MANAGER_APP_DATA, payload: { bidProfiles: [...profiles] } });
            toastr.success(i18n.t('shiftBidding:toastr.deletingBid'), i18n.t('shiftBidding:toastr.bidDeletedSuccessfully'));
        } else {
            toastr.error(i18n.t('shiftBidding:toastr.deletingBid'), i18n.t('shiftBidding:toastr.failedToDelete'));
        }
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.deletingBid'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {string} profileId
 * @param {BidProfile.IBriefBaseProfile[]} profiles
 * @param {BidProfile.IBriefBaseProfile[]} archivedProfiles
 * @returns
 */
export const archiveBidProfile = (profileId, profiles, archivedProfiles) => /** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const response = await api.archiveBidProfile(profileId);

        if (response.status === 200) {
            const bidProfile = profiles.find((x) => x.id === profileId);
            // @ts-ignore
            archivedProfiles.push(bidProfile);
            const bidProfileIndex = profiles.findIndex((x) => x.id === profileId);
            profiles.splice(bidProfileIndex, 1);
            dispatch({ type: UPDATE_MANAGER_APP_DATA, payload: { bidProfiles: [...profiles] } });
            dispatch({ type: UPDATE_MANAGER_APP_DATA, payload: { archivedBidProfiles: [...archivedProfiles] } });
            toastr.success(i18n.t('shiftBidding:toastr.archivingBid'), i18n.t('shiftBidding:toastr.bidArchivedSuccessfully'));
        } else {
            toastr.error(i18n.t('shiftBidding:toastr.archivingBid'), i18n.t('shiftBidding:toastr.failedToArchive'));
        }
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.archivingBid'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {string} profileId
 * @param {{
 *  bidProfileDrafts: BidProfile.IBriefBaseProfile[]
 *  hyperfinds: ManagerAppData.IHyperfind[],
 *  managerBidSetups: ManagerAppData.IManagerBidSetup[],
 * }} storeData
 * @returns
 */
export const duplicateBidProfile = (profileId, storeData) => /** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
    const {
        bidProfileDrafts,
    } = storeData;
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const response = await api.duplicateBidProfile(profileId);
        if (response.status === 200) {
            const [duplicatedDraft] = simplifyBidProfileDraftModels([response.data], storeData);
            const duplicatedDraftBrief = BriefBidProfileModelBuilder.fromBidProfileDraftPresentationModel(response.data, storeData);
            dispatch({
                type: UPDATE_MANAGER_APP_DATA,
                payload: { 
                    bidProfileDrafts: [...bidProfileDrafts, duplicatedDraftBrief], 
                    pageName: i18n.t('shiftBidding:manager.biddingList.createNewBid'),
                    dataviewEmployeeData: [],
                    filteredDataviewEmployeeData: [],
                    dataviewFields: [],
                } 
            });
            dispatch({ type: UPDATE_DRAFT_BID_PROFILE, payload: duplicatedDraft });
            dispatch({
                type: UPDATE_DRAFT_EMPLOYEES_SELECTION,
                payload: { 
                    employeesIds: [], 
                    hyperfindId: undefined, 
                    hyperfindLabel: '' 
                } 
            });
            dispatch({
                type: UPDATE_DRAFT_ORIGINAL_EMPLOYEES_SELECTION,
                payload: { 
                    employeesIds: [], 
                    hyperfindId: undefined, 
                    hyperfindLabel: '' 
                }
            });
            dispatch(push('/Manager/form'));
            toastr.success(i18n.t('shiftBidding:toastr.duplicatingBid'), i18n.t('shiftBidding:toastr.bidDuplicatedSuccessfully'));
        } else {
            toastr.error(i18n.t('shiftBidding:toastr.duplicatingBid'), i18n.t('shiftBidding:toastr.failedToSave'));
        }
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.duplicatingBid'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {string} profileId
 * @param {{
 *  bidProfiles: BidProfile.IBriefBaseProfile[]
 *  bidProfileDrafts: BidProfile.IBriefBaseProfile[]
 *  hyperfinds: ManagerAppData.IHyperfind[],
 *  managerBidSetups: ManagerAppData.IManagerBidSetup[],
 * }} storeData
 * @returns
 */
export const rebidBidProfile = (profileId, storeData) => /** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
    const {
        bidProfiles,
        bidProfileDrafts,
    } = storeData;
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const response = await api.rebidBidProfile(profileId);
        if (response.status === 200) {
            // Create draft of latest rebid
            const [rebidDraft] = simplifyBidProfileDraftModels([response.data], storeData);
            // Load schedule templates
            const scheduleTemplatesResponse = await api.getBidProfileScheduleItemsDraft(rebidDraft.draftId);
            if (scheduleTemplatesResponse.status === 200) {
                const canEditScheduleSelection = isScheduleSelectionEditable(rebidDraft.status);
                const scheduleTemplates = simplifyScheduleTemplates(scheduleTemplatesResponse.data, canEditScheduleSelection);
                dispatch({ type: UPDATE_DRAFT_SCHEDULE_SELECTION, payload: scheduleTemplates });
                dispatch({ type: UPDATE_DRAFT_ORIGINAL_SCHEDULE_SELECTION, payload: _.cloneDeep(scheduleTemplates) });
            }
            const briefRebidDraft = BriefBidProfileModelBuilder.fromBidProfileDraftPresentationModel(response.data, storeData);
            rebidDraft.bidProfileInformation.isExpanded = true;
            dispatch({ type: UPDATE_MANAGER_APP_DATA, payload: { bidProfileDrafts: [...bidProfileDrafts, briefRebidDraft], pageName: i18n.t('shiftBidding:manager.biddingList.createNewBid') } });
            dispatch({ type: UPDATE_DRAFT_BID_PROFILE, payload: rebidDraft });

            // Delete previous 'latest' bid profile from store
            const bidProfileIndex = bidProfiles.findIndex((x) => x.id === profileId);
            bidProfiles.splice(bidProfileIndex, 1);
            dispatch({ type: UPDATE_MANAGER_APP_DATA, payload: { bidProfiles: [...bidProfiles] } });

            dispatch(push('/Manager/form'));
            toastr.success(i18n.t('shiftBidding:toastr.creatingRebid'), i18n.t('shiftBidding:toastr.rebidCreatedSuccessfully'));
        } else {
            toastr.error(i18n.t('shiftBidding:toastr.creatingRebid'), i18n.t('shiftBidding:toastr.failedToSave'));
        }
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.creatingRebid'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {string} profileId
 * @param {{
 *  bidProfiles: BidProfile.IBriefBaseProfile[],
 *  bidProfileInformation: BidProfile.IBidProfileInformationAccordion,
 * }} storeData
 * @returns
 */
export const saveBidInformation = (profileId, storeData) => /** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    const { bidProfiles, bidProfileInformation } = storeData;
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const response = await api.updateBiddingInformation(profileId, bidProfileInformation);
        if (response.status === 200) {
            const latestStatus = response.data.status;
            dispatch({
                type: UPDATE_BID_PROFILE,
                payload: { status: latestStatus },
            });
            dispatch({
                type: UPDATE_ORIGINAL_BIDDING_INFORMATION,
                payload: _.cloneDeep(bidProfileInformation),
            });
            dispatch({
                type: UPDATE_BIDDING_INFORMATION,
                payload: {
                    isExpanded: false,
                },
            });
            const indexToUpdate = bidProfiles.findIndex((x) => x.id === profileId);
            bidProfiles[indexToUpdate] = BriefBidProfileModelBuilder.updateBidInformationInBriefProfile(bidProfiles[indexToUpdate], response.data);
            dispatch({ type: UPDATE_MANAGER_APP_DATA, payload: { bidProfiles } });
            dispatch({ type: UPDATE_SCHEDULE_SELECTION, payload: { isExpanded: true } });
        } else {
            toastr.error(i18n.t('shiftBidding:toastr.savingBidInformation'), i18n.t('shiftBidding:toastr.failedToSave'));
        }
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.savingBidInformation'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 *
 * @param {string} profileId
 * @param {ScheduleSource} scheduleSource
 * @param {{
 *  bidProfiles: BidProfile.IBriefBaseProfile[],
 *  scheduleTemplates: BidProfile.IScheduleTemplatesAccordion,
 * }} storeData
 */
export const saveScheduleTemplateSelections = (profileId, scheduleSource, storeData) => /** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
    const { bidProfiles, scheduleTemplates } = storeData;
    /**
     * @type {number[] | undefined}
     */
    let selectedIds = [];
    /**
     * @param {BidProfile.IScheduleTemplateItem[] | undefined} items
     * @returns {number[]}
     */
    const getSelectedScheduleTemplateIds = (items) => (items ? items?.filter((x) => x.isSelected).map((x) => x.id) : []);
    if (scheduleSource === ScheduleSource.ExcelFile) {
        selectedIds = getSelectedScheduleTemplateIds(scheduleTemplates?.items);
    } else {
        selectedIds = scheduleTemplates?.retrievedWorkplans?.workplans.flatMap((x) => getSelectedScheduleTemplateIds(x.scheduleTemplates?.items));
    }

    if (!selectedIds || !selectedIds.length) {
        toastr.error(i18n.t('shiftBidding:toastr.savingScheduleTemplates'), i18n.t('shiftBidding:toastr.pleaseSelectAtLeastOneScheduleTemplate'));
        return;
    }
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const response = await api.SelectSchedulePatternTemplates(profileId, selectedIds);
        if (response.status === 200) {
            const latestStatus = response.data.status;
            dispatch({
                type: UPDATE_BID_PROFILE,
                payload: { status: latestStatus, managerWarnings: response.data.managerWarnings },
            });
            dispatch({ type: UPDATE_ORIGINAL_SCHEDULE_SELECTION, payload: _.cloneDeep(scheduleTemplates) });
            dispatch({ type: UPDATE_SCHEDULE_SELECTION, payload: { isExpanded: false } });
            const indexToUpdate = bidProfiles.findIndex((x) => x.id === profileId);
            bidProfiles[indexToUpdate] = BriefBidProfileModelBuilder.updateScheduleTemplatesInBriefProfile(bidProfiles[indexToUpdate], response.data, false);
            dispatch({ type: UPDATE_MANAGER_APP_DATA, payload: { bidProfiles } });
            dispatch({ type: UPDATE_EMPLOYEES_SELECTION, payload: { isExpanded: true } });
        }
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.savingScheduleTemplates'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {string} profileId
 * @param {string} bidName
 * @returns
 */
export const downloadExcelTemplateFile = (profileId, bidName) => /** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const response = await api.DownloadExcelTemplate(profileId);

        if (response.status === 200) {
            FileService.downloadContentInFile(`${bidName}_SchedulePatternTemplate`, 'csv', response.data);
        } else {
            toastr.error(i18n.t('shiftBidding:toastr.downloadingCSVFile'), i18n.t('shiftBidding:toastr.failedToDownload'));
        }
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.downloadingCSVFile'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {string} profileId
 * @param {any} excelFile
 * @returns
 */
export const importExcelFile = (profileId, excelFile) => /** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const response = await api.UploadExcelScheduleTemplates(profileId, excelFile);
        if (response.status === 200) {
            dispatch({
                type: UPDATE_BID_PROFILE,
                payload: {
                    isUploadingSchedule: response.data.isUploadingSchedule
                }
            });
            toastr.success(i18n.t('shiftBidding:toastr.importingExcelFile'), i18n.t('shiftBidding:toastr.importProcessStarted'));
        } else {
            toastr.error(i18n.t('shiftBidding:toastr.importingExcelFile'), i18n.t('shiftBidding:toastr.failedToSave'));
        }
    } catch (error) {
        if (error?.response?.data?.Message) {
            const fileName = excelFile.name.split('.').slice(0, -1).join('.');
            const count = (error?.response?.data?.Message.match(/\r\n/g) || [1]).length;
            toastr.error(i18n.t('shiftBidding:toastr.importingExcelFile'), i18n.t('shiftBidding:toastr.importErrorsDetected', { count }));
            FileService.downloadContentInFile(`${fileName}Errors`, 'txt', error.response.data.Message);
        } else {
            toastr.error(i18n.t('shiftBidding:toastr.importingExcelFile'), i18n.t('shiftBidding:toastr.failedToSave'));
        }
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {string} profileId
 * @param {string[]} workplanIds
 */
export const retrieveWorkplans = (profileId, workplanIds) =>/** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const response = await api.selectWorkplans(profileId, workplanIds);

        if (response.status === 200) {
            const scheduleTemplates = simplifyScheduleTemplates(response.data, true);
            scheduleTemplates.isExpanded = true;
            dispatch({ type: UPDATE_SCHEDULE_SELECTION, payload: scheduleTemplates });
            dispatch({ type: UPDATE_ORIGINAL_SCHEDULE_SELECTION, payload: _.cloneDeep(scheduleTemplates) });
            dispatch({ type: UPDATE_BID_PROFILE, payload: { isWorkPlansRetrieved: true } });
            toastr.success(i18n.t('shiftBidding:toastr.retrievingScheduleTemplates'), i18n.t('shiftBidding:toastr.retrieveSuccessful'));
        } else {
            dispatch({ type: UPDATE_BID_PROFILE, payload: { isWorkPlansRetrieved: false } });
            toastr.error(i18n.t('shiftBidding:toastr.retrievingScheduleTemplates'), i18n.t('shiftBidding:toastr.failedToSave'));
        }
    } catch (error) {
        dispatch({ type: UPDATE_BID_PROFILE, payload: { isWorkPlansRetrieved: false } });
        toastr.error(i18n.t('shiftBidding:toastr.retrievingScheduleTemplates'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {string} profileId
 * @param {{
 *  bidProfiles: BidProfile.IBriefBaseProfile[],
 *  employees: BidProfile.IEmployeeSelectionAccordion,
 *  hyperfinds: ManagerAppData.IHyperfind[],
 *  scheduleTemplates: BidProfile.IScheduleTemplatesAccordion,
 * }} storeData
 */
export const saveEmployeeSelectionStep = (profileId, storeData) => /** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
    const {
        bidProfiles, employees, hyperfinds, scheduleTemplates,
    } = storeData;
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const response = await api.updateEmployeeSelection(profileId, {
            ...employees,
            periodStartDate: applyUkgUserTimeZoneTransformation(employees.periodSelected.startDate).toISOString() || '',
            periodEndDate: applyUkgUserTimeZoneTransformation(employees.periodSelected.endDate).toISOString() || '',
        });
        if (response.status === 200) {
            const latestStatus = response.data.status;
            dispatch({
                type: UPDATE_BID_PROFILE,
                payload: { status: latestStatus, managerWarnings: response.data.managerWarnings },
            });
            dispatch({
                type: UPDATE_ORIGINAL_EMPLOYEES_SELECTION,
                payload: {
                    ..._.cloneDeep(employees),
                    rankedEmployees: response.data.employeeInfos.rankedEmployees,
                },
            });
            const hasManagementAccess = findHasManagementAccess({ status: latestStatus, isManagementAllowed: response.data.isManagementAllowed });
            if (hasManagementAccess) {
                const managementInfoResponse = await api.getBidProfileManagementInfos(profileId);
                dispatch({
                    type: UPDATE_BID_PROFILE_MANAGEMENT,
                    payload: {
                        profileId: _.cloneDeep(profileId),
                        employeesBidSelection: managementInfoResponse.data.employeesBidSelection,
                        awardingResults: simplifyAwardingResult(
                            managementInfoResponse.data.awardingResults,
                            scheduleTemplates.items,
                        ),
                        isBidOnBehalfAllowed: managementInfoResponse.data.isBidOnBehalfAllowed,
                        isEditBidOnBehalfAllowed: managementInfoResponse.data.isEditBidOnBehalfAllowed,
                        isRecalculateEmployeeRanksAllowed: managementInfoResponse.data.isRecalculateEmployeeRanksAllowed,
                    },
                });
            }

            const { sequentialSchedule, submissionPeriodEndDate } = response.data.schedulingAndVisibility;
            if (sequentialSchedule && Object.keys(sequentialSchedule).length !== 0) {
                const payload = {
                    sequentialSchedule: simplifySequentialSchedule(sequentialSchedule),
                    submissionPeriodEndDate: convertNullableDateTimeToUkgUserDateTime(submissionPeriodEndDate),
                };
                dispatch({
                    type: UPDATE_BID_PROFILE_SCHEDULE,
                    payload,
                });
                dispatch({
                    type: UPDATE_ORIGINAL_BID_PROFILE_SCHEDULE,
                    payload,
                });
            }

            dispatch({ type: UPDATE_EMPLOYEES_SELECTION, payload: { isExpanded: false } });
            dispatch({ type: UPDATE_BID_PROFILE_SCHEDULE, payload: { isExpanded: true } });
            const indexToUpdate = bidProfiles.findIndex((x) => x.id === profileId);
            bidProfiles[indexToUpdate] = BriefBidProfileModelBuilder.updateEmployeesInBriefProfile(
                bidProfiles[indexToUpdate],
                response.data,
                false,
                { hyperfinds },
            );
            return dispatch({ type: UPDATE_MANAGER_APP_DATA, payload: { bidProfiles } });
        }
        toastr.error(i18n.t('shiftBidding:toastr.savingBidEmployees'), i18n.t('shiftBidding:toastr.failedToSave'));
        return Promise.reject;
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.savingBidEmployees'), error.response.data.Message);
        return Promise.reject;
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {string} profileId
 * @param {{
 *  bidProfiles: BidProfile.IBriefBaseProfile[],
 *  bidProfile: BidProfile.IBidProfile,
 * }} storeData
 * @param {boolean} redirectsToSummary
 */
export const applySchedule = (profileId, storeData, redirectsToSummary = true) => /** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
    const { bidProfiles, bidProfile } = storeData;
    const { schedule } = bidProfile;
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        // Check if the request period end date is "forever" (year 3000) to avoid timezone transformation issues
        const requestPeriodEndDate = isForeverDate(schedule.requestPeriodEndDate)
            ? FOREVER_ISO_STRING
            : removeUkgUserTimeZoneTransformation(schedule.requestPeriodEndDate).toISOString();
        const response = await api.updateschedulePeriod(profileId, {
            requestPeriodStartDate: removeUkgUserTimeZoneTransformation(schedule.requestPeriodStartDate).toISOString(),
            requestPeriodEndDate,
            submissionPeriodStartDate: removeUkgUserTimeZoneTransformation(schedule.submissionPeriodStartDate).toISOString(),
            submissionPeriodEndDate: removeUkgUserTimeZoneTransformation(schedule.submissionPeriodEndDate).toISOString(),
            dayOfTheWeek: Object.keys(schedule.dayOfTheWeek).filter((x) => schedule.dayOfTheWeek[x]).map((x) => parseInt(x, 10)),
            startBiddingTime: removeUkgUserTimeZoneTransformation(schedule.startBiddingTime).toISOString(),
            endBiddingTime: removeUkgUserTimeZoneTransformation(schedule.endBiddingTime).toISOString(),
            timePerEmployeeUnit: schedule.timePerEmployeeUnit,
            timePerEmployeeValue: schedule.timePerEmployeeValue,
            isAnytime: schedule.isAnytime,
            isVisibilityEnabled: false, // TODO R3
            daysBeforeVisibility: 0, // TODO R3
        });
        if (response.status === 200) {
            const { status: latestStatus, isForeverRequestPeriod } = response.data;
            dispatch({
                type: UPDATE_BID_PROFILE,
                payload: { status: latestStatus, isForeverRequestPeriod },
            });
            dispatch({ type: UPDATE_ORIGINAL_BID_PROFILE_SCHEDULE, payload: { ..._.cloneDeep(schedule) } });

            const indexToUpdate = bidProfiles.findIndex((x) => x.id === profileId);
            bidProfiles[indexToUpdate] = BriefBidProfileModelBuilder.updateScheduleInBriefProfile(bidProfiles[indexToUpdate], response.data);
            dispatch({ type: UPDATE_MANAGER_APP_DATA, payload: { bidProfiles } });

            if (redirectsToSummary) {
                dispatch({
                    type: UPDATE_BID_PROFILE_SCHEDULE,
                    payload: {
                        isExpanded: false,
                    },
                });
                dispatch({
                    type: UPDATE_BID_PROFILE,
                    payload: {
                        tabSelected: BidProfileEditTab.Summary,
                    },
                });
            }
            dispatch({ type: UPDATE_MANAGER_APP_DATA, payload: { pageName: i18n.t('shiftBidding:manager.common.manageBids') } });
        } else {
            toastr.error(i18n.t('shiftBidding:toastr.savingBidEmployees'), i18n.t('shiftBidding:toastr.failedToSave'));
        }
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.savingBidEmployees'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {string} profileId
 * @param {{
 *  schedule: BidProfile.IBidProfileSchedulingAccordion,
 *  employees: BidProfile.IEmployeeSelection,
 * }} storeData
 */
export const getSequentialEmployeeSchedule = (profileId, storeData) => /** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
    const { schedule, employees } = storeData;
    if (!employees.employeesIds.length) {
        toastr.error(i18n.t('shiftBidding:toastr.calculatingEstimatedSubmissionEndDate'), i18n.t('shiftBidding:toastr.pleaseSelectAtLeastOneEmployee'));
        return Promise.reject;
    }
    try {
        const response = await api.sequentialEmployeeSchedule(profileId, {
            // @ts-ignore - If able to save then schedule values should not be null!
            submissionPeriodStartDate: removeUkgUserTimeZoneTransformation(schedule.submissionPeriodStartDate).toISOString(),
            dayOfTheWeek: Object.keys(schedule.dayOfTheWeek).filter((x) => schedule.dayOfTheWeek[x]).map((x) => parseInt(x, 10)),
            startBiddingTime: removeUkgUserTimeZoneTransformation(schedule.startBiddingTime).toISOString(),
            endBiddingTime: removeUkgUserTimeZoneTransformation(schedule.endBiddingTime).toISOString(),
            timePerEmployeeUnit: schedule.timePerEmployeeUnit,
            timePerEmployeeValue: schedule.timePerEmployeeValue,
            isAnytime: schedule.isAnytime,
        });
        if (response.status === 200) {
            const parsedSequentialSchedule = response.data?.employeesSchedule;
            if (parsedSequentialSchedule) {
                const payload = {
                    sequentialSchedule: simplifySequentialSchedule(parsedSequentialSchedule),
                    submissionPeriodEndDate: convertNullableDateTimeToUkgUserDateTime(response.data?.submissionPeriodEndDate),
                };
                return dispatch({
                    type: UPDATE_BID_PROFILE_SCHEDULE,
                    payload,
                });
            }
            return Promise.resolve;
        }
        toastr.error(i18n.t('shiftBidding:toastr.calculatingEstimatedSubmissionEndDate'), i18n.t('shiftBidding:toastr.failedToEstimate'));
        return Promise.reject;
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:toastr.calculatingEstimatedSubmissionEndDate'), error.response.data.Message);
        return Promise.reject;
    }
};

/**
 * @param {string} profileId
 */
export const updateRetrieveImportScheduleCompleted = (profileId) =>/** @param {import('redux').Dispatch} dispatch */ async (dispatch) => {
    // Update this first so that we stop pinging /status
    dispatch({
        type: UPDATE_BID_PROFILE,
        payload: { isUploadingSchedule: false },
    });

    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const scheduleTemplatesResponse = await api.GetBidProfileScheduleItems(profileId);

        if (scheduleTemplatesResponse.status === 200) {
            const response = await api.getBidProfile(profileId);
            const scheduleTemplates = simplifyScheduleTemplates(scheduleTemplatesResponse.data, true);
            scheduleTemplates.isExpanded = true;
            dispatch({ type: UPDATE_SCHEDULE_SELECTION, payload: scheduleTemplates });
            dispatch({ type: UPDATE_ORIGINAL_SCHEDULE_SELECTION, payload: _.cloneDeep(scheduleTemplates) });
            dispatch({
                type: UPDATE_BID_PROFILE,
                payload: {
                    isWorkPlansRetrieved: false, // TOOD - what is this?
                    anchorDay: response.data.anchorDay,
                    isUploadingSchedule: response.data.isUploadingSchedule
                }
            });

            /* No workplan stuff in bid
            const { bidProfiles } = storeData;
            const draft = bidProfiles.find((x) => x.draftId === draftId);
            if (draft?.scheduleSource === ScheduleSource.WorkplanManager && scheduleTemplatesResponse.data.isActiveWorkplanBid && draftResponse.data?.schedulingAndVisibility?.requestPeriodEndDate) {
                // @ts-ignore
                const workplanStartDate = convertUtcDateToDateInUserTimezone(scheduleTemplatesResponse.data.activeWorkplanStartDate * 1000);
                let { requestPeriodStartDate } = draftResponse.data.schedulingAndVisibility;
                if (!requestPeriodStartDate || workplanStartDate >= requestPeriodStartDate) {
                    requestPeriodStartDate = workplanStartDate;
                }
                const requestPeriodEndDate = applyUkgUserTimeZoneTransformation(draftResponse.data.schedulingAndVisibility.requestPeriodEndDate);

                dispatch({
                    type: UPDATE_BID_PROFILE_SCHEDULE,
                    payload: { requestPeriodStartDate, requestPeriodEndDate },
                });
                dispatch({
                    type: UPDATE_ORIGINAL_BID_PROFILE_SCHEDULE,
                    payload: { requestPeriodStartDate, requestPeriodEndDate },
                });

                draft.isForeverRequestPeriod = scheduleTemplatesResponse.data.activeWorkplanEndDate === FOREVER_EPOCH;
            }
            */
        } else {
            dispatch({ type: UPDATE_DRAFT_BID_PROFILE, payload: { isWorkPlansRetrieved: false } });
            toastr.error(i18n.t('shiftBidding:toastr.retrievingScheduleTemplates'), i18n.t('shiftBidding:toastr.failedToSave'));
        }
    } catch (error) {
        dispatch({ type: UPDATE_DRAFT_BID_PROFILE, payload: { isWorkPlansRetrieved: false } });
        toastr.error(i18n.t('shiftBidding:toastr.retrievingScheduleTemplates'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};
